import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import ChartActions from '../chartActions/ChartActions';
import { useAppSelector } from 'src/hooks/redux';
import { selectModelData } from '../../selectors/selectors';
import Loading from 'src/components/Loading';
import { getDefaultReportData, processReportDataByQuartersWithActions, ReportAnswer } from '../../utils/reportActions';
import { selectReportAnswers, selectReportAnswersLoading, selectReportCombinedLoading } from '../../store/reports/selectors';
import { selectLinkedActionsLoading, selectLinkedActionsOnly } from '../../store/link-actions/selectors';

interface ReportActionsProps {
  data: any;
  activeDirection: number | null;
  onDirectionChange: (directionId: number) => void;
  isFocal?: boolean;
  availableDirections?: any[];
  selectedFactoryIds?: number[];
}

function ReportActions({ data, activeDirection, onDirectionChange, isFocal, availableDirections = [], selectedFactoryIds = [] }: ReportActionsProps) {
  const isInternalUpdate = useRef(false);
  const [localActiveDirection, setLocalActiveDirection] = useState<number | null>(activeDirection);

  const storeDirections = useAppSelector((state: any) => selectModelData(state, 'gop-directions'));

  const dataDirection = useMemo(() => {
    return availableDirections.length > 0 ? availableDirections : storeDirections;
  }, [availableDirections, storeDirections]);

  const reportAnswers = useAppSelector(selectReportAnswers) as ReportAnswer[];
  const linkedActions = useAppSelector(selectLinkedActionsOnly);
  const isLoadingAnswers = useAppSelector(selectReportAnswersLoading);
  const isLoadingActions = useAppSelector(selectLinkedActionsLoading);
  const isLoadingCombined = useAppSelector(selectReportCombinedLoading);
  // console.log('linkedActions',linkedActions)
  const allDataReady = useMemo(() => {
    if (isLoadingCombined || isLoadingAnswers || isLoadingActions) {
      return false;
    }
    const answersReady = reportAnswers && reportAnswers.length > 0 && !isLoadingAnswers;
    const actionsReady = (linkedActions && linkedActions.length > 0) || !isLoadingActions;

    const loadingFinished = !isLoadingAnswers && !isLoadingActions && !isLoadingCombined;
    const hasNoData = (!reportAnswers || reportAnswers.length === 0) && (!linkedActions || linkedActions.length === 0);

    return (answersReady && actionsReady) || (loadingFinished && hasNoData);
  }, [reportAnswers, linkedActions, isLoadingAnswers, isLoadingActions, isLoadingCombined]);

const filteredLinkedActions = useMemo(() => {
  return linkedActions.filter((action) => {
    if (localActiveDirection && Number(action.linkedReportAction?.directionID) !== Number(localActiveDirection)) {
      return false;
    }
    if (isFocal !== undefined) {
      if (action.linkedReportAction?.IsFocal !== isFocal) {
        return false;
      }
    }
    if (selectedFactoryIds.length > 0) {
      const actionFactoryId = action.linkedReportAction?.factory?.id;
      if (actionFactoryId && !selectedFactoryIds.includes(actionFactoryId)) {
        return false;
      }
    }
    return true;
  });
}, [linkedActions, localActiveDirection, isFocal, selectedFactoryIds]);

  const processedReportData = useMemo(() => {
    if (!reportAnswers || reportAnswers.length === 0) {
      return getDefaultReportData();
    }

    return processReportDataByQuartersWithActions(reportAnswers, filteredLinkedActions);
  }, [reportAnswers, filteredLinkedActions]);

  const handleDirectionChange = useCallback(
    (directionId: number) => {
      isInternalUpdate.current = true;
      setLocalActiveDirection(directionId);
      onDirectionChange?.(directionId);
      Promise.resolve().then(() => {
        isInternalUpdate.current = false;
      });
    },
    [onDirectionChange]
  );

  useEffect(() => {
    if (!isInternalUpdate.current && activeDirection !== localActiveDirection) {
      setLocalActiveDirection(activeDirection);
    }
  }, [activeDirection, localActiveDirection]);

  useEffect(() => {
    if (dataDirection && dataDirection.length > 0 && localActiveDirection === null) {
      const firstDirectionId = dataDirection[0].id;
      handleDirectionChange(firstDirectionId);
    }
  }, [dataDirection, localActiveDirection, handleDirectionChange]);

  const isLoading = !dataDirection || dataDirection.length === 0;

  if (isLoading) {
    return (
      <div className='rounded-lg bg-white p-6 shadow-md'>
        <div className='flex items-center justify-center h-64'>
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className='rounded-lg bg-white p-6 shadow-md'>
      <div className='mb-6'>
        <div className='flex justify-between gap-1 shadow-md p-3 rounded-lg text-sm overflow-x-auto'>
          {dataDirection.map((direction: any) => (
            <button
              key={direction.id}
              onClick={() => handleDirectionChange(direction.id)}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                localActiveDirection === direction.id ? 'bg-[#F9FAFB] text-[#012169] shadow font-semibold' : 'text-[#637381] hover:text-gray-700'
              }`}
            >
              {direction.Title}
              {/* <div className='flex flex-col'>
                {direction.Title}
                <span className='text-xs font-light '>{direction.gop[0].title}</span>
              </div> */}
            </button>
          ))}
        </div>
      </div>

      {(isLoadingCombined || !allDataReady) && (
        <div className='flex items-center justify-center h-64'>
          <Loading />
        </div>
      )}

      {allDataReady && (
        <div className='bg-gray-50 rounded-lg p-4'>
          <ChartActions data={processedReportData.data} openActions={processedReportData.openActions} closeActions={processedReportData.closeActions} />
        </div>
      )}
    </div>
  );
}

export default React.memo(ReportActions);
